import { createSelector } from 'reselect';
import dayjs from 'dayjs';

const EMPTY_CLIENTS = [];
const EMPTY_BAD = [];

const selectClientsState = (state) => state.clientReducer;
const selectClientsRaw = (state) =>
  Array.isArray(state.clientReducer?.clients) ? state.clientReducer.clients : EMPTY_CLIENTS;

export const getPrenotazioneByID = () =>
  createSelector(selectClientsState, (state) => state.prenotazione);

export const getClient = () =>
  createSelector(selectClientsState, (state) => ({
    ...state?.activeClient?.cliente,
    name: `${state.activeClient?.cliente?.nome} ${state.activeClient?.cliente?.cognome}`,
    value: state.activeClient?.cliente?.idutente,
    icon: state.activeClient?.cliente?.img,
    phone: state.activeClient?.cliente?.cellulare,
    phoneVerified: Boolean(Number(state.activeClient?.cliente?.cellulare_verificato)),
    email: state.activeClient?.cliente?.email,
    registrationDate: state.activeClient?.cliente?.data_iscrizione,
    lastAccess: state.activeClient?.cliente?.ultimo_accesso,
    favoriteDay: state.activeClient?.giornoPreferito,
    lastAppointment: state.activeClient?.ultimaPrenotazione,
    favoriteService: state.activeClient?.servizioPreferito,
    workerAppointment: state.activeClient?.poltronePrenotate,
    appointmentPerMonth: state.activeClient?.prenPerMese,
    favoriteTimes: state.activeClient?.orarioPreferito,
    tipo: state.activeClient?.cliente?.tipo,
    appointments: state.activeClient?.prenotazioni?.map((appointment) => ({
      id: appointment.idprenotazione,
      startDate: appointment.data_prenotazione,
      startTime: appointment.orario_start,
      worker: appointment.poltrona,
      worker_id: appointment.poltrona,
      services: (appointment.servizi || []).map((service) => service.nomeServizio),
    })),
    notes: state.activeClient?.notes,
  }));

const mapClientsShort = (clients) =>
  clients.map((client) => ({
    idutente: client.idutente,
    name: `${client.nome} ${client.cognome}`,
    value: client.idutente,
    icon: client.img,
    type: client.tipo,
    phone: client.cellulare,
    email: client.email,
    category: client.tipo,
    totAppointments: client.totPren,
    lastAppointment: client.lastPren,
    createFromSalone: client.createFromSalone,
    verified: client.cellulare_verificato,
    ultimo_accesso: client.ultimaAperturaHomePage ?? client.ultimo_accesso,
    disabilitato: client.disabilitato ?? 0,
  }));

/** Stable selector — do not recreate with getClientsShort() each render. */
export const selectClientsShort = createSelector(selectClientsRaw, mapClientsShort);

export const getClientsShort = () => selectClientsShort;

const selectAllClientsRaw = (state) =>
  Array.isArray(state.clientReducer?.allClients) ? state.clientReducer.allClients : EMPTY_CLIENTS;

/** Elenco completo per prenotazione: non usa la pagina di GET_CLIENTS_PLUS. */
export const selectAllClientsShort = createSelector(selectAllClientsRaw, mapClientsShort);

export const getAllClientsShort = () => selectAllClientsShort;

export const selectClientsListMeta = createSelector(selectClientsState, (state) => state.listMeta || {
  page: 1,
  limit: 20,
  total: 0,
  totalPages: 0,
});

export const selectClientsListStats = createSelector(
  selectClientsState,
  (state) => state.listStats || null,
);

const clientSearchFields = (client) =>
  [client.name, client.nome, client.cognome, client.email, client.phone, client.cellulare]
    .filter((value) => value != null && String(value).trim() !== '')
    .map((value) => String(value).trim().toLowerCase());

/** Match full query or every whitespace-separated token against name/contact fields. */
export const matchesClientSearch = (client, searchedName = '') => {
  const query = searchedName.trim().toLowerCase();
  if (!query) return true;

  const fields = clientSearchFields(client);
  if (!fields.length) return false;

  const combined = fields.join(' ');
  if (combined.includes(query)) return true;

  const tokens = query.split(/\s+/).filter(Boolean);
  if (tokens.length === 1) {
    return fields.some((field) => field.includes(tokens[0]));
  }

  return tokens.every((token) => fields.some((field) => field.includes(token)));
};

/**
 * Backend list search matches single tokens on nome/cognome; a full "nome cognome" string often misses.
 * For multi-word queries we fetch with the first token and refine client-side.
 */
export const normalizeClientSearchForApi = (search = '') => {
  const trimmed = String(search).trim();
  if (!trimmed) return '';
  const tokens = trimmed.split(/\s+/).filter(Boolean);
  if (tokens.length <= 1) return trimmed;
  return tokens[0];
};

const matchesSearch = matchesClientSearch;

/** Same buckets as the header counters — never drop a counted client from the table. */
export const classifyClientActivity = (client, oneMonthAgo = dayjs().subtract(30, 'day')) => {
  const disabled = Number(client.disabilitato) === 1;
  const lastAccess = dayjs(client.ultimo_accesso);
  const recentlyActive = !disabled && !lastAccess.isBefore(oneMonthAgo);

  return {
    disabled,
    recentlyActive,
    inactive: !disabled && !recentlyActive,
    fromSalon: Number(client.createFromSalone) === 1,
    isNew: recentlyActive && Number(client.totAppointments) < 3,
  };
};

export const matchesClientsListFilter = (
  client,
  filterId,
  { oneMonthAgo = dayjs().subtract(30, 'day'), query = '' } = {},
) => {
  if (!matchesSearch(client, query)) return false;

  const activity = classifyClientActivity(client, oneMonthAgo);

  switch (filterId) {
    case 0:
      return true;
    case 1:
      return activity.recentlyActive;
    case 2:
      return activity.inactive;
    case 3:
      return activity.disabled;
    case 4:
      return activity.fromSalon;
    case 5:
      return activity.isNew;
    default:
      return true;
  }
};

export const buildClientsStats = (clients, searchedName = '') => {
  const oneMonthAgo = dayjs().subtract(30, 'day');
  const stats = {
    registeredClients: 0,
    activeClients: 0,
    nonActiveClients: 0,
    disabledClients: 0,
    fromSalonClients: 0,
    newClients: 0,
  };

  for (let i = 0; i < clients.length; i += 1) {
    const client = clients[i];
    if (!matchesSearch(client, searchedName)) continue;

    stats.registeredClients += 1;
    const activity = classifyClientActivity(client, oneMonthAgo);
    if (activity.fromSalon) stats.fromSalonClients += 1;
    if (activity.disabled) {
      stats.disabledClients += 1;
      continue;
    }
    if (activity.recentlyActive) {
      stats.activeClients += 1;
      if (activity.isNew) stats.newClients += 1;
    } else {
      stats.nonActiveClients += 1;
    }
  }

  return stats;
};

/** @deprecated Prefer buildClientsStats(selectClientsShort result, search) */
export const getClientsStats = (searchedName) =>
  createSelector(selectClientsShort, (clients) => buildClientsStats(clients, searchedName));

export const selectBadClients = createSelector(
  selectClientsState,
  (state) => (Array.isArray(state.badClients) ? state.badClients : EMPTY_BAD),
);

export const allBadClients = () => selectBadClients;
